import { Routes, UrlMatchResult, UrlSegment } from '@angular/router';
import { nativeTrackingGuard } from './features/tracking/tracking.guard';
import { authGuard, securityAdministratorGuard } from '@core/guards/auth.guard';
import { MetroMapService } from './features/map/metro-map.service';
import { EAccessCode } from './app.enums';
import { provideState } from '@ngrx/store';
import { PvsTaskReducer } from './features/pvs-task/state/pvs-task.reducer';
import { PvsTaskEffects } from './features/pvs-task/state/pvs-task.effects';
import { provideEffects } from '@ngrx/effects';
import { EventListReducer } from './features/event-list/state/event-list.reducer';
import { EventListEffects } from './features/event-list/state/event-list.effects';
import { EventCardEffects } from './features/event-card/state/event-card.effects';
import { EventCardReactiveEffects } from './features/event-card/state/event-card-reactive.effects';
import { EventCardReducer } from './features/event-card/state/event-card.reducer';
import {
  preserveChatBackContext,
  preserveChatVoiceRecording,
} from './features/chat-page/chat-page.guard';
import { firstAllowedContextGuard } from './core/guards/first-allowed-context.guard';
import {
  APP_ROUTE_REQUIRED_ACCESS_DATA_KEY,
  AppAccessRequirement,
  AppNavigationContext,
  getAppNavigationContextDescriptor,
} from './core/models/app-navigation.model';
import { provideSupervisorDashboard } from './features/supervisor-dashboard/data-access/provide-supervisor-dashboard';
import {
  appAccessAvailabilityGuard,
  getAppAccessGuard,
} from './core/guards/app-access.guard';
import {
  accessUnavailableServicePageGuard,
  forbiddenServicePageGuard,
} from './core/guards/service-page.guard';

/**
 * Получить access guard
 *
 * Обертка для типизации ключей
 * @param value
 * @param multi
 * @param redirectTo
 * @returns
 */
const getAccessGuard = (
  value: EAccessCode | AppAccessRequirement['codes'],
  multi?: 'every' | 'some',
  redirectTo = '/forbidden',
) => {
  const codes: AppAccessRequirement['codes'] =
    typeof value === 'string' ? [value] : value;

  return getAppAccessGuard({ codes, mode: multi ?? 'every' }, redirectTo);
};

const getContextAccessGuard = (context: AppNavigationContext) => {
  const requirement = getAppNavigationContextDescriptor(context).access;
  if (requirement === null) {
    throw new Error(`Navigation context does not require access: ${context}`);
  }
  const { codes, mode } = requirement;

  return getAccessGuard(codes, mode);
};

const getContextRoutePath = (context: AppNavigationContext): string =>
  getAppNavigationContextDescriptor(context).route.slice(1);

const getContextRouteData = (context: AppNavigationContext) => {
  const descriptor = getAppNavigationContextDescriptor(context);
  return {
    activeContext: descriptor.context,
    [APP_ROUTE_REQUIRED_ACCESS_DATA_KEY]: descriptor.access,
  };
};

function chatPageMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  if (segments.length === 0) {
    return { consumed: [] };
  }

  if (segments.length === 1) {
    return {
      consumed: segments,
      posParams: { reference: segments[0] },
    };
  }

  return null;
}

export const routes: Routes = [
  {
    path: 'security-administrator',
    canActivate: [securityAdministratorGuard],
    loadComponent: () =>
      import('./features/auth/security-administrator/security-administrator.component').then(
        (c) => c.SecurityAdministratorComponent,
      ),
  },
  {
    path: 'auth-code',
    loadComponent: () =>
      import('./features/auth/auth.component').then((c) => c.AuthComponent),
  },
  {
    path: '',
    canActivate: [authGuard, appAccessAvailabilityGuard],
    data: { bottomChrome: true },
    children: [
      {
        path: '',
        pathMatch: 'full',
        canActivate: [firstAllowedContextGuard],
        children: [],
      },
      {
        path: getContextRoutePath('analytics'),
        canActivate: [getContextAccessGuard('analytics')],
        data: { ...getContextRouteData('analytics'), createEventFab: false },
        loadComponent: () =>
          import('./features/scenario-modeling/scenario-modeling.component').then(
            (c) => c.ScenarioModelingComponent,
          ),
      },
      {
        path: getContextRoutePath('dashboard'),
        canActivate: [getContextAccessGuard('dashboard')],
        data: {
          ...getContextRouteData('dashboard'),
          createEventFab: false,
        },
        loadComponent: () =>
          import('./features/supervisor-dashboard/dashboard.component').then(
            (c) => c.DashboardComponent,
          ),
        providers: provideSupervisorDashboard(),
      },
      {
        path: getContextRoutePath('events'),
        canActivate: [getContextAccessGuard('events')],
        loadComponent: () =>
          import('./features/event-list/event-list.component').then(
            (c) => c.EventListComponent,
          ),
        providers: [
          provideState('eventList', EventListReducer),
          provideEffects(EventListEffects),
        ],
        data: getContextRouteData('events'),
        children: [
          {
            path: ':id',
            canActivate: [getAccessGuard(EAccessCode.s_NnrSbCard)],
            data: {
              createEventFab: false,
              [APP_ROUTE_REQUIRED_ACCESS_DATA_KEY]: {
                codes: [EAccessCode.s_NnrSbCard],
                mode: 'every',
              } satisfies AppAccessRequirement,
            },
            loadComponent: () =>
              import('./features/event-card/event-card.component').then(
                (c) => c.EventCardComponent,
              ),
            providers: [
              provideState('eventCard', EventCardReducer),
              provideEffects(EventCardEffects, EventCardReactiveEffects),
            ],
            children: [
              {
                path: '',
                pathMatch: 'full',
                loadComponent: () =>
                  import('./features/event-card/event-card-info/event-card-info.component').then(
                    (c) => c.EventCardInfoComponent,
                  ),
              },
            ],
          },
        ],
      },
      {
        path: 'stations/:stationId/events',
        canActivate: [getContextAccessGuard('events')],
        data: {
          activeContext: 'map',
          [APP_ROUTE_REQUIRED_ACCESS_DATA_KEY]:
            getAppNavigationContextDescriptor('events').access,
        },
        loadComponent: () =>
          import('./features/station-event-list/station-event-list.component').then(
            (c) => c.StationEventListComponent,
          ),
        providers: [
          provideState('eventList', EventListReducer),
          provideEffects(EventListEffects),
        ],
      },
      {
        path: getContextRoutePath('map'),
        canActivate: [getContextAccessGuard('map')],
        data: getContextRouteData('map'),
        loadComponent: () =>
          import('./features/map/map.component').then((c) => c.MapComponent),
        providers: [
          MetroMapService,
          provideState('eventList', EventListReducer),
          provideEffects(EventListEffects),
        ],
      },
      {
        path: getContextRoutePath('chat'),
        data: getContextRouteData('chat'),
        children: [
          {
            matcher: chatPageMatcher,
            canDeactivate: [
              preserveChatVoiceRecording,
              preserveChatBackContext,
            ],
            loadComponent: () =>
              import('./features/chat-page/chat-page.component').then(
                (c) => c.ChatPageComponent,
              ),
          },
        ],
      },
      {
        path: 'tracking',
        canActivate: [
          getAccessGuard(EAccessCode.s_Tracking),
          nativeTrackingGuard,
        ],
        data: {
          activeContext: null,
          [APP_ROUTE_REQUIRED_ACCESS_DATA_KEY]: {
            codes: [EAccessCode.s_Tracking],
            mode: 'every',
          },
        },
        loadComponent: () =>
          import('./features/tracking/tracking.component').then(
            (c) => c.TrackingComponent,
          ),
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./features/menu/app-settings/app-settings.component').then(
            (c) => c.AppSettingsComponent,
          ),
        data: { activeContext: null },
      },
      {
        path: 'help',
        loadComponent: () =>
          import('./features/menu/help/help.component').then(
            (c) => c.HelpComponent,
          ),
        data: { activeContext: null },
      },
      {
        path: getContextRoutePath('tasks'),
        canActivate: [getContextAccessGuard('tasks')],
        data: {
          ...getContextRouteData('tasks'),
          createEventFab: false,
        },
        loadComponent: () =>
          import('./features/pvs-task/pvs-task.component').then(
            (c) => c.PvsTaskComponent,
          ),
        providers: [
          provideState('pvsTask', PvsTaskReducer),
          provideEffects(PvsTaskEffects),
        ],
      },
      {
        path: getContextRoutePath('pvs-shift'),
        canActivate: [getContextAccessGuard('pvs-shift')],
        data: {
          ...getContextRouteData('pvs-shift'),
          createEventFab: false,
        },
        loadComponent: () =>
          import('./features/pvs-shift/pvs-shift.component').then(
            (c) => c.PvsShiftComponent,
          ),
      },
    ],
  },
  {
    path: 'not-found',
    data: { kind: 'not-found' },
    loadComponent: () =>
      import('./features/service-page/service-page.component').then(
        (c) => c.ServicePageComponent,
      ),
  },
  {
    path: 'forbidden',
    canActivate: [authGuard, forbiddenServicePageGuard],
    data: { kind: 'forbidden' },
    loadComponent: () =>
      import('./features/service-page/service-page.component').then(
        (c) => c.ServicePageComponent,
      ),
  },
  {
    path: 'access-unavailable',
    canActivate: [authGuard, accessUnavailableServicePageGuard],
    data: { kind: 'access-unavailable' },
    loadComponent: () =>
      import('./features/service-page/service-page.component').then(
        (c) => c.ServicePageComponent,
      ),
  },
  { path: '**', redirectTo: 'not-found' },
];
